import { apiClient } from './apiClient';

// 게시글 인터페이스
export interface Post {
  id: number;
  title: string;
  content: string;
  author: string;
  category: string;
  createdAt: string;
  updatedAt?: string;
  visible: boolean;
  views: number;
  commentsCount: number;
  authorId: number;
}

// 게시글 목록 조회 파라미터
export interface PostListParams {
  page?: number;
  size?: number;
  postCategoryId?: number | null; // null 값도 허용
  from?: string; // yyyy-mm-dd 형식
  to?: string; // yyyy-mm-dd 형식
  status?: string; // visible, hidden 등
  search?: string;
}

// 게시글 목록 응답
export interface PostListResponse {
  content: Post[];
  totalPages: number;
  totalElements: number;
  currentPage: number;
  size: number;
}

// API 응답에서 받는 실제 데이터 형식
interface PostApiResponse {
  post_id: number;
  title: string;
  category_name: string;
  writer_name: string;
  created_at: string;
  visible: boolean;
}

// API 응답 인터페이스
interface ApiResponse<T> {
  code: number;
  status: string;
  message: string;
  data: T;
}

// 페이지네이션 응답 인터페이스 (실제 API 응답 구조)
interface PaginatedApiResponse {
  content: PostApiResponse[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  totalElements: number;
  totalPages: number;
  last: boolean;
  size: number;
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  numberOfElements: number;
  first: boolean;
  empty: boolean;
}

// API 응답을 Post 인터페이스로 변환하는 함수
const convertToPost = (apiPost: PostApiResponse): Post => ({
  id: apiPost.post_id,
  title: apiPost.title,
  content: '', // API에서 제공하지 않음
  author: apiPost.writer_name,
  category: apiPost.category_name,
  createdAt: apiPost.created_at,
  visible: apiPost.visible,
  views: 0, // API에서 제공하지 않음
  commentsCount: 0, // API에서 제공하지 않음
  authorId: 0, // API에서 제공하지 않음
});

// 게시글 API 서비스
export const postApi = {
  // 게시글 목록 조회 - GET /api/admin/posts/list/user
  getPosts: async (params: PostListParams = {}): Promise<PostListResponse> => {
    try {
      /// console.log('게시글 목록 조회 API 호출:', params);
      
      // 쿼리 파라미터 구성 (기존 형식)
      const queryParams = new URLSearchParams();
      
      if (params.page !== undefined) {
        queryParams.append('page', params.page.toString());
      }
      if (params.size !== undefined) {
        queryParams.append('size', params.size.toString());
      }
      if (params.postCategoryId) {
        queryParams.append('postCategoryId', params.postCategoryId.toString());
      }
      if (params.from) {
        queryParams.append('from', params.from);
      }
      if (params.to) {
        queryParams.append('to', params.to);
      }
      if (params.status) {
        queryParams.append('visible', params.status === 'visible' ? 'true' : 'false');
      }
      if (params.search) {
        queryParams.append('search', params.search);
      }
      
      const queryString = queryParams.toString();
      const url = `/api/admin/posts/list/user${queryString ? `?${queryString}` : ''}`;
      
      const { data: result } = await apiClient.get<ApiResponse<PaginatedApiResponse>>(url, {
        withCredentials: true
      });
      
      /// console.log('게시글 목록 API 응답:', {result});
      
      if (result.code !== 200) {
        throw new Error(`API error: ${result.message}`);
      }
      
      // API 응답 데이터를 Post 인터페이스로 변환
      const convertedPosts = result.data.content.map(convertToPost);
      
      return {
        content: convertedPosts,
        totalPages: result.data.totalPages,
        totalElements: result.data.totalElements,
        currentPage: result.data.number,
        size: result.data.size
      };
    } catch (error) {
      /// console.error('게시글 목록 조회 실패:', error);
      throw error;
    }
  },

  // 게시글 가시성 토글 - PATCH /api/admin/posts/{postId}/visible
  togglePostVisibility: async (postId: number, visible: boolean): Promise<void> => {
    try {
      /// console.log('게시글 가시성 토글 API 호출:', postId, visible);
      
      const queryParams = new URLSearchParams();
      queryParams.append('visible', visible.toString());
      
      const url = `/api/admin/posts/${postId}/visible?${queryParams.toString()}`;
      
      const { data: result } = await apiClient.patch<ApiResponse<any>>(
        url, 
        {},
        { withCredentials: true }
      );
      
      /// console.log('게시글 가시성 토글 API 응답:', {result});
      
      if (result.code !== 200) {
        throw new Error(result.message);
      }
    } catch (error: any) {
      /// console.error('게시글 가시성 토글 실패:', error);
      throw new Error(error.response?.data?.message || error.message || '게시글 상태 변경 중 오류가 발생했습니다.');
    }
  },

  // 게시글 삭제 - DELETE /api/admin/posts/{postId} (hard delete)
  deletePost: async (postId: number, reason?: string): Promise<void> => {
    try {
      /// console.log('게시글 삭제 API 호출:', postId, reason);
      
      const { data: result } = await apiClient.delete<ApiResponse<any>>(
        `/api/admin/posts/${postId}`,
        {
          data: reason ? { reason } : undefined,
          withCredentials: true
        }
      );
      
      /// console.log('게시글 삭제 API 응답:', {result});
      
      if (result.code !== 200) {
        throw new Error(result.message);
      }
    } catch (error: any) {
      /// console.error('게시글 삭제 실패:', error);
      throw new Error(error.response?.data?.message || error.message || '게시글 삭제 중 오류가 발생했습니다.');
    }
  }
};